import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { colors } from './theme';
import { deviceCount, repairProgress } from './utils/rmaDevices';
import RmaBundleModal from './components/RmaBundleModal.jsx';
import { useUser, useAuth } from '@clerk/clerk-react';
import RmaModalPending from './components/RmaModalPending.jsx';
import RmaModalAssigned from './components/RmaModalAssigned.jsx';
import RmaModalReceived from './components/RmaModalReceived.jsx';
import RmaModalRepairing from './components/RmaModalRepairing.jsx';
import RmaModalReady from './components/RmaModalReady.jsx';
import RmaModalShipped from './components/RmaModalShipped.jsx';
import RmaModalClosedCase from './components/RmaModalClosedCase.jsx';
import LoadingSpinner from './components/LoadingSpinner';
import SubmitComplaintModal from './components/SubmitComplaintModal.jsx';
import RmaHistoryPanel from './components/RmaHistoryPanel.jsx';
import RmaBoardFilters from './components/RmaBoardFilters.jsx';
import { tagsFor, filterOptions, applyFilters, hasActiveFilter, EMPTY_FILTERS } from './utils/rmaTags';
import { collectRoleTokens } from './roles';
import NoAccessNotice from './components/NoAccessNotice';

const API_BASE = process.env.REACT_APP_API_URL;

const STATUS_FLOW = [
  { key: 'pending', label: 'Pending' },
  { key: 'assigned', label: 'Assigned' },
  { key: 'received', label: 'Received' },
  { key: 'awaiting_repair', label: 'Awaiting Repair' },
  { key: 'repairing', label: 'Repairing' },
  { key: 'awaiting_shipping', label: 'Awaiting Shipping' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'closed_case', label: 'Closed' },
  { key: 'archived', label: 'Archived' }
];

// Only expose active workflow steps on the dashboard board. Archived RMAs live
// in dedicated archive views and are intentionally excluded here to avoid a
// dormant column on the board.
export const DASHBOARD_STATUS_FLOW = STATUS_FLOW.filter(
  ({ key }) => key !== 'archived'
);
export const DASHBOARD_STATUS_ORDER = DASHBOARD_STATUS_FLOW.map(step => step.key);
const DASHBOARD_STATUS_SET = new Set(DASHBOARD_STATUS_ORDER);

const STATUS_ALIASES = {
  Pending: 'pending',
  Assigned: 'assigned',
  Received: 'received',
  'Awaiting Repair Start': 'awaiting_repair',
  Assessment: 'awaiting_repair',
  'Repair In Progress': 'repairing',
  Repairing: 'repairing',
  'Repair Finished': 'awaiting_shipping',
  Ready: 'awaiting_shipping',
  'Awaiting Shipping Details': 'awaiting_shipping',
  'Awaiting Dispatch Confirmation': 'awaiting_shipping',
  Shipped: 'shipped',
  ArchivedCaseClosed: 'archived',
  Closed: 'archived',
  'Repair Cancelled by Customer': 'archived',
  cancelled: 'archived',
  closed_case: 'closed_case'
};

const STATUS_LABELS = STATUS_FLOW.reduce((acc, step) => {
  acc[step.key] = step.label;
  return acc;
}, {});

const MODAL_COMPONENTS = {
  pending: RmaModalPending,
  assigned: RmaModalAssigned,
  received: RmaModalReceived,
  awaiting_repair: RmaModalRepairing,
  repairing: RmaModalRepairing,
  awaiting_shipping: RmaModalReady,
  shipped: RmaModalShipped,
  archived: RmaModalShipped,
  closed_case: RmaModalClosedCase
};

const normalizeStatusKey = status => {
  if (!status) return 'pending';
  const trimmed = typeof status === 'string' ? status.trim() : status;
  if (!trimmed) return 'pending';
  const alias = STATUS_ALIASES[trimmed];
  if (alias) return alias;
  const lowered = typeof trimmed === 'string' ? trimmed.toLowerCase() : trimmed;
  return STATUS_LABELS[lowered] ? lowered : lowered;
};

const normalizeRma = rma => {
  if (!rma || typeof rma !== 'object') {
    return rma;
  }
  const normalizedStatus = normalizeStatusKey(rma.status);
  return {
    ...rma,
    status: normalizedStatus
  };
};

export const filterVisibleDashboardRmas = items =>
  Array.isArray(items)
    ? items.filter(rma => rma && normalizeStatusKey(rma.status) !== 'archived')
    : [];

export default function RmaDashboard() {
  const [rmas, setRmas] = useState([]);
  const [activeRma, setActiveRma] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancelled, setShowCancelled] = useState(false);
  const [showBundle, setShowBundle] = useState(false);
  const [escalateRma, setEscalateRma] = useState(null);
  const [showHistory, setShowHistory] = useState(true);

  // Remembered so a refresh, or coming back from a case, does not drop you to
  // the full board again (issue #749).
  const [filters, setFilters] = useState(() => {
    try {
      const saved = window.localStorage.getItem('rmaBoardFilters');
      return saved ? { ...EMPTY_FILTERS, ...JSON.parse(saved) } : EMPTY_FILTERS;
    } catch {
      return EMPTY_FILTERS;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem('rmaBoardFilters', JSON.stringify(filters));
    } catch {
      // A browser refusing storage is not a reason to break the board.
    }
  }, [filters]);
  const { user, isSignedIn } = useUser();
  const { getToken } = useAuth();
  // collectRoleTokens, not publicMetadata.role: a role can be stamped as a
  // string OR an array, and reading only the string turned colleagues away from
  // the board while they passed every other guard in the app (issue #755).
  const roles = collectRoleTokens(user);
  const isAdmin = roles.includes('admin');
  const isService = roles.some(r => r.startsWith('service'));

  useEffect(() => {
    if (!isAdmin && !isService) return;
    const fetchRmas = async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/rmas`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        const normalized = Array.isArray(data) ? data.map(normalizeRma) : [];
        setRmas(filterVisibleDashboardRmas(normalized));
      } catch (err) {
        console.error('Error fetching RMAs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchRmas();
  }, [isAdmin, isService, getToken]);

  const handleModalClose = () => setActiveRma(null);

  const handleUpdate = (updatedRma, options = {}) => {
    const normalized = normalizeRma(updatedRma);
    setRmas(prev => {
      const withoutCurrent = prev.filter(r => r._id !== normalized._id);
      if (normalizeStatusKey(normalized.status) === 'archived') {
        return withoutCurrent;
      }
      return [...withoutCurrent, normalized];
    });
    if (options.keepOpen) {
      setActiveRma(normalized);
    } else {
      setActiveRma(null);
    }
  };

  // Options come from what is on the board, so the filter never offers a value
  // that would return nothing.
  const options = useMemo(() => filterOptions(rmas), [rmas]);
  const filtering = hasActiveFilter(filters);
  const matchingCount = useMemo(
    () => (filtering ? applyFilters(rmas, filters).length : rmas.length),
    [rmas, filters, filtering]
  );

  const groupedRmas = DASHBOARD_STATUS_ORDER.reduce((acc, status) => {
    acc[status] = [];
    return acc;
  }, {});

  rmas.forEach(rma => {
    const statusKey = normalizeStatusKey(rma.status);
    if (!DASHBOARD_STATUS_SET.has(statusKey)) {
      return;
    }
    if (!groupedRmas[statusKey]) {
      groupedRmas[statusKey] = [];
    }
    groupedRmas[statusKey].push(rma);
  });

  const canceledRmas = rmas.filter(rma => {
    const key = normalizeStatusKey(rma.status);
    return key === 'canceled_by_customer' || key === 'cancelled';
  });

  const formatDate = value => {
    if (!value) return null;
    try {
      return new Date(value).toLocaleDateString('en-CA');
    } catch (err) {
      return null;
    }
  };

  const ModalComponent = activeRma ? MODAL_COMPONENTS[activeRma.status] : null;

  const renderStatusIcon = status => {
    switch (status) {
      case 'pending':
        return <span style={{ color: colors.statusYellow }}>●</span>;
      case 'assigned':
        return <span style={{ color: colors.muted }}>●</span>;
      case 'received':
        return <span style={{ color: colors.statusYellow }}>●</span>;
      case 'awaiting_repair':
        return <span style={{ color: colors.statusYellow }}>🕒</span>;
      case 'repairing':
        return <span style={{ color: colors.statusYellow }}>🛠</span>;
      case 'awaiting_shipping':
        return <span style={{ color: colors.statusYellow }}>📦</span>;
      case 'shipped':
        return <span style={{ color: colors.success }}>🚚</span>;
      case 'closed_case':
        return <span style={{ color: colors.danger }}>🔒</span>;
      case 'archived':
        return <span style={{ color: colors.muted }}>🗄</span>;
      default:
        return null;
    }
  };

  if (!isSignedIn || (!isAdmin && !isService)) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <NoAccessNotice pageName="the service board" />
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LoadingSpinner size={64} />
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', padding: '1.25rem 1.5rem 0' }}>
        <header className="ni-header" style={{ paddingLeft: 0, paddingRight: 0, flexShrink: 0 }}>
          <div className="ni-brand">
            <Link to="/" title="Back to main page" aria-label="Back to main page" style={{ display: 'inline-flex', lineHeight: 0 }}>
              <img src="/north-invent-logo.svg" alt="North Invent" className="ni-brand-logo" />
            </Link>
            <div className="ni-brand-copy">
              <span className="ni-eyebrow">RMA Flow</span>
              <span className="ni-brand-subtitle">Live service handling board</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <Link
            to="/service/refurbished"
            className="ni-button ni-button--ghost"
            style={{ fontSize: '0.85rem', textDecoration: 'none' }}
          >
            Refurbished units
          </Link>
          <button
            type="button"
            onClick={() => setShowBundle(true)}
            className="ni-button ni-button--ghost"
            style={{ fontSize: '0.85rem' }}
            title="Handle two cases from the same customer as one shipment"
          >
            Bundle cases
          </button>
          <button
            type="button"
            onClick={() => setShowCancelled(prev => !prev)}
            className={`ni-button ${showCancelled ? 'ni-button--ghost' : 'ni-button--muted'}`}
            style={{
              ...toggleButtonStyle,
              opacity: canceledRmas.length === 0 && !showCancelled ? 0.6 : 1,
              cursor: canceledRmas.length === 0 && !showCancelled ? 'not-allowed' : 'pointer'
            }}
            disabled={canceledRmas.length === 0 && !showCancelled}
          >
            {showCancelled ? 'Back to active board' : 'Canceled & closed cases'}
          </button>
          </div>
        </header>

        {showBundle && (
          <RmaBundleModal
            rmas={rmas}
            onClose={() => setShowBundle(false)}
            onBundled={(updated, mergedRmaId) => {
              // The absorbed case leaves the board; the keeper gains its units.
              setRmas(prev => prev
                .filter(r => r.rmaId !== mergedRmaId)
                .map(r => (r._id === updated._id ? updated : r)));
              setShowBundle(false);
            }}
          />
        )}

        {showCancelled ? (
          <div className="ni-grid ni-grid--three">
            {canceledRmas.length === 0 ? (
              <div className="ni-panel ni-empty">No canceled cases.</div>
            ) : (
              canceledRmas.map(rma => {
                const approvalInfo = rma.repair?.customerApproval || {};
                const isPremature = normalizeStatusKey(rma.status) === 'cancelled';
                const cancellation = rma.cancellation || {};
                const canceledAt = formatDate(
                  isPremature ? cancellation.at : (approvalInfo.decidedAt || cancellation.at)
                );
                return (
                  <div key={rma._id || rma.rmaId} style={canceledCardStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span style={{ fontWeight: 600 }}>{rma.rmaId}</span>
                      {/* The live board never shows a test case -- flagging one
                          closes it, which moves it here. So this is the only
                          place the tag can actually do its job (issues #747/#749). */}
                      {rma.testInstance && <span style={testTagStyle}>TEST</span>}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>{rma.serialNumber}</div>
                    <div style={{ fontSize: '0.85rem', color: colors.textDim }}>
                      {canceledAt ? `Closed ${canceledAt}` : 'Cancellation recorded'}
                    </div>
                    {isPremature ? (
                      <>
                        <div style={{ fontSize: '0.72rem', color: '#fb923c', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          Closed prematurely{cancellation.stage ? ` · at ${STATUS_LABELS[cancellation.stage] || cancellation.stage}` : ''}
                        </div>
                        {cancellation.reason && (
                          <div style={{ fontSize: '0.9rem', color: colors.textBody }}>
                            {cancellation.reason}
                          </div>
                        )}
                        {cancellation.by && (
                          <div style={{ fontSize: '0.78rem', color: colors.textMuted }}>
                            by {cancellation.by}
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ fontSize: '0.9rem', color: colors.textBody }}>
                        Customer canceled repair.
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        ) : (
          <>
          <RmaBoardFilters
            filters={filters}
            onChange={setFilters}
            options={options}
            matching={matchingCount}
            total={rmas.length}
          />
          <div style={boardStyle}>
            {DASHBOARD_STATUS_FLOW.map(({ key, label }) => {
              const allInLane = groupedRmas[key] || [];
              const laneRmas = applyFilters(allInLane, filters);
              return (
              <div key={key} style={columnStyle}>
                {/* A lane that scrolls hides how full it is, so say the count.
                    While filtering, say how much of the lane is being hidden. */}
                <div style={columnHeader}>
                  {label}
                  {allInLane.length > 0 && (
                    <span style={laneCountStyle}>
                      {filtering ? `${laneRmas.length}/${allInLane.length}` : laneRmas.length}
                    </span>
                  )}
                </div>
                <div style={columnScrollStyle}>
                {laneRmas.map(rma => {
                  const isClosedLane = key === 'closed_case';
                  const devices = deviceCount(rma);
                  return (
                    <div
                      key={rma.rmaId}
                      onClick={() => setActiveRma(rma)}
                      style={cardStyle}
                    >
                      {/* overflow:hidden is what stops this column painting over
                          the controls beside it (issues #726/#727). */}
                      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                        <div style={{ fontWeight: 'bold' }}>{rma.rmaId}</div>
                        <div style={serialRowStyle}>
                          <span style={cardTextClip} title={rma.serialNumber}>{rma.serialNumber}</span>
                          {devices > 1 && (
                            <span style={deviceCountBadge}>{deviceCount(rma)} devices</span>
                          )}
                        </div>
                        {(() => {
                          // Derived from the case, never typed: where the unit
                          // is, and whose it is (issue #749).
                          const tags = tagsFor(rma);
                          if (!tags.length) return null;
                          return (
                            <div style={tagRowStyle}>
                              {tags.map(tag => (
                                <span
                                  key={`${tag.kind}-${tag.key}`}
                                  style={tag.kind === 'test' ? testTagStyle : tag.kind === 'customer' ? customerTagStyle : locationTagStyle}
                                  title={tag.kind === 'test' ? 'Test case — kept out of the retrieval corpus. Not yet excluded from the quality figures.' : tag.kind === 'customer' ? `Customer: ${tag.label}` : `Location: ${tag.label}`}
                                >
                                  {tag.kind === 'location' || tag.kind === 'internal' || tag.kind === 'partner' ? `⌂ ${tag.label}` : tag.label}
                                </span>
                              ))}
                            </div>
                          );
                        })()}
                        {key === 'repairing' && devices > 1 && (() => {
                          const p = repairProgress(rma);
                          return (
                            <div style={{ fontSize: '0.68rem', color: p.done === p.total ? '#46bb89' : '#fb923c', marginTop: '0.2rem', fontWeight: 600 }}>
                              {p.done}/{p.total} units finished
                            </div>
                          );
                        })()}
                        {rma.supplierEscalation?.complaintNumber && (
                          <div style={{ ...cardTextClip, fontSize: '0.68rem', color: colors.primary, marginTop: '0.2rem', fontWeight: 600 }}>
                            NCR: {rma.supplierEscalation.complaintNumber}
                          </div>
                        )}
                        {isClosedLane && (() => {
                          const rs = rma.refurbishment?.status;
                          const refurbId = rma.refurbishment?.refurbId;
                          if (!rs || rs === 'pending') return (
                            <div style={{ ...cardTextClip, fontSize: '0.72rem', color: '#fb923c', marginTop: '0.25rem' }}>Awaiting decision</div>
                          );
                          if (rs === 'scrapped') return (
                            <div style={{ ...cardTextClip, fontSize: '0.72rem', color: '#f87171', marginTop: '0.25rem' }}>Scrapped · {refurbId}</div>
                          );
                          if (rs === 'repairing') return (
                            <div style={{ ...cardTextClip, fontSize: '0.72rem', color: '#fb923c', marginTop: '0.25rem' }}>Refurb in progress · {refurbId}</div>
                          );
                          if (rs === 'recommissioned') return (
                            <div style={{ ...cardTextClip, fontSize: '0.72rem', color: '#46bb89', marginTop: '0.25rem' }}>Recommissioned · {refurbId}</div>
                          );
                          return null;
                        })()}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.3rem', flexShrink: 0 }}>
                        {isClosedLane ? (
                          <button
                            type="button"
                            onClick={event => { event.stopPropagation(); setActiveRma(rma); }}
                            className="ni-button ni-button--primary"
                            style={refurbishButtonStyle}
                          >
                            {rma.refurbishment?.decision ? 'View' : 'Refurbish'}
                          </button>
                        ) : (
                          <div>{renderStatusIcon(key)}</div>
                        )}
                        {!rma.supplierEscalation?.complaintNumber && (
                          <button
                            type="button"
                            title="Escalate to supplier"
                            onClick={event => { event.stopPropagation(); setEscalateRma(rma); }}
                            className="ni-button ni-button--ghost"
                            style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem', opacity: 0.7 }}
                          >
                            Escalate
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
                </div>
              </div>
              );
            })}
          </div>
          </>
        )}

        {ModalComponent && activeRma && (
          <>
            <ModalComponent
              rma={activeRma}
              onClose={handleModalClose}
              onSave={handleUpdate}
            />
            <RmaHistoryPanel
              rma={activeRma}
              open={showHistory}
              onToggle={() => setShowHistory(prev => !prev)}
            />
          </>
        )}

        {escalateRma && (
          <SubmitComplaintModal
            linkedRma={escalateRma}
            prefill={{
              supplier: 'gpv',
              title: `Supplier escalation — ${escalateRma.rmaId}`,
              serialNumber: escalateRma.serialNumber || '',
              productNumber: escalateRma.productNumber || '',
              description: `Escalated from RMA case ${escalateRma.rmaId}.`,
            }}
            onClose={() => setEscalateRma(null)}
            onCreated={(complaint) => {
              // Update the local RMA card to show the NCR badge
              setRmas(prev => prev.map(r =>
                r._id === escalateRma._id
                  ? { ...r, supplierEscalation: { complaintNumber: complaint.complaintId, complaintId: complaint._id } }
                  : r
              ));
              setEscalateRma(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

export { normalizeStatusKey, normalizeRma };


const boardStyle = {
  display: 'flex',
  gap: '1rem',
  overflowX: 'auto',
  // Sideways only. Height is bounded by the page shell so the lanes scroll
  // internally rather than dragging the board off the bottom (issue #727).
  overflowY: 'hidden',
  flex: 1,
  minHeight: 0,
  padding: '1rem 0 1.5rem',
};

const columnStyle = {
  flex: '1 0 250px',
  backgroundColor: colors.surface,
  border: `1px solid ${colors.panelBorder}`,
  borderRadius: '18px',
  display: 'flex',
  flexDirection: 'column',
  // No minHeight: every lane is now as tall as the board, so a busy lane and an
  // empty one line up instead of one setting the height for all of them.
  overflow: 'hidden',
  boxShadow: colors.shadow
};

// The cards. Stays a flex column so the cards' margins do not collapse into
// each other the way they would as plain block siblings.
const columnScrollStyle = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  display: 'flex',
  flexDirection: 'column'
};

const columnHeader = {
  background: 'rgba(70,187,137,0.1)',
  color: colors.primary,
  padding: '0.8rem 0.9rem',
  textAlign: 'center',
  fontWeight: '600',
  borderBottom: `1px solid rgba(70,187,137,0.18)`,
  borderTopLeftRadius: '18px',
  borderTopRightRadius: '18px',
  flexShrink: 0
};

// How many cases are in a lane you can only see part of.
const laneCountStyle = {
  marginLeft: '0.4rem',
  fontSize: '0.72rem',
  fontWeight: 700,
  color: colors.textMuted,
  background: 'rgba(255,255,255,0.06)',
  borderRadius: 999,
  padding: '0.05rem 0.45rem'
};

// One line of card text, clipped so it can never paint over the controls to its
// right. Long serials and refurb IDs end in an ellipsis instead (issue #726).
const cardTextClip = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

// The serial and its device badge share a row; the serial absorbs the squeeze.
const serialRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
  minWidth: 0,
  fontSize: '0.8rem',
  color: colors.textMuted
};

// Marks a case that covers several service tags (issue #712).
const deviceCountBadge = {
  fontSize: '0.66rem',
  fontWeight: 700,
  color: colors.primary,
  background: 'rgba(70,187,137,0.14)',
  border: '1px solid rgba(70,187,137,0.35)',
  borderRadius: 999,
  padding: '0.05rem 0.4rem',
  whiteSpace: 'nowrap',
  // Never truncate the badge itself -- a card reading "2 de..." was the
  // original complaint.
  flexShrink: 0,
};

const cardStyle = {
  backgroundColor: colors.surfaceRaised,
  margin: '0.5rem',
  flexShrink: 0,
  gap: '0.5rem',
  padding: '0.75rem',
  borderRadius: '14px',
  boxShadow: '0 10px 24px rgba(0,0,0,0.16)',
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  color: colors.textStrong,
  border: `1px solid rgba(255,255,255,0.04)`
};

// Derived tags (issue #749). Wraps, so a long customer name cannot push the
// card wider or collide with the controls beside it.
const tagRowStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.25rem',
  marginTop: '0.3rem',
  minWidth: 0
};

const baseTagStyle = {
  fontSize: '0.63rem',
  fontWeight: 600,
  borderRadius: 999,
  padding: '0.05rem 0.4rem',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

const locationTagStyle = {
  ...baseTagStyle,
  color: '#93c5fd',
  background: 'rgba(59,130,246,0.12)',
  border: '1px solid rgba(59,130,246,0.3)'
};

const testTagStyle = {
  ...baseTagStyle,
  color: '#fbbf24',
  background: 'rgba(251,191,36,0.12)',
  border: '1px solid rgba(251,191,36,0.35)',
  letterSpacing: '0.05em'
};

const customerTagStyle = {
  ...baseTagStyle,
  color: colors.textMuted,
  background: 'rgba(255,255,255,0.06)',
  border: `1px solid ${colors.panelBorder}`
};

const refurbishButtonStyle = {
  fontWeight: 600,
  minHeight: '40px'
};

const toggleButtonStyle = {
  fontSize: '0.9rem'
};

const canceledCardStyle = {
  backgroundColor: colors.surface,
  border: `1px solid ${colors.panelBorder}`,
  borderRadius: '18px',
  padding: '1rem',
  boxShadow: colors.shadow,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.35rem',
  color: colors.textStrong
};

// Exported for the layout regression tests. Lane bounding and card text
// clipping regress silently -- nothing throws, the board just goes wrong again
// (issues #726/#727).
export {
  boardStyle,
  columnStyle,
  columnScrollStyle,
  cardStyle,
  cardTextClip,
  serialRowStyle,
  deviceCountBadge
};
